/**
 * Owner: Masa Bayu
 * File: natural-reader-bookmarks.ts
 * Date:Time: 2026-10-17 19:20
 * Description: QX Natural Reader 05 — bookmark halaman per buku (persist
 *   localStorage, app-side — format QXP tidak diusik). Corak sample-access-store.
 */

export const NATURAL_BOOKMARKS_STORAGE_KEY = 'qxread.natural-reader-bookmarks.v1';

type BookmarkMap = Record<string, readonly number[]>;

function sanitize(input: unknown): BookmarkMap {
  if (typeof input !== 'object' || input === null) {
    return {};
  }
  const map: BookmarkMap = {};
  for (const [key, value] of Object.entries(input)) {
    if (!Array.isArray(value)) {
      continue;
    }
    const pages = value
      .filter((entry): entry is number => typeof entry === 'number' && Number.isFinite(entry))
      .map((entry) => Math.max(1, Math.round(entry)));
    map[key] = Object.freeze([...new Set(pages)].sort((a, b) => a - b));
  }
  return map;
}

function readMap(): BookmarkMap {
  try {
    const raw = window.localStorage.getItem(NATURAL_BOOKMARKS_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    return sanitize(JSON.parse(raw));
  } catch {
    return {};
  }
}

function writeMap(map: BookmarkMap): void {
  try {
    window.localStorage.setItem(NATURAL_BOOKMARKS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage penuh/ditolak — kekal dalam memori sesi ini.
  }
}

export function listNaturalBookmarks(publicationId: string): readonly number[] {
  return readMap()[publicationId] ?? Object.freeze([] as number[]);
}

export function isNaturalBookmarked(publicationId: string, pageIndex: number): boolean {
  return listNaturalBookmarks(publicationId).includes(pageIndex);
}

export function toggleNaturalBookmark(publicationId: string, pageIndex: number): readonly number[] {
  const trimmed = publicationId.trim();
  if (trimmed.length === 0 || !Number.isFinite(pageIndex)) {
    return Object.freeze([] as number[]);
  }
  const map = readMap();
  const current = [...(map[trimmed] ?? [])];
  const page = Math.max(1, Math.round(pageIndex));
  const next = current.includes(page)
    ? current.filter((entry) => entry !== page)
    : [...current, page].sort((a, b) => a - b);
  const frozen = Object.freeze(next);
  map[trimmed] = frozen;
  writeMap(map);
  return frozen;
}

export function removeNaturalBookmark(
  publicationId: string,
  pageIndex: number,
): readonly number[] {
  const map = readMap();
  const current = map[publicationId] ?? [];
  const frozen = Object.freeze(current.filter((entry) => entry !== pageIndex));
  map[publicationId] = frozen;
  writeMap(map);
  return frozen;
}

export function resetNaturalBookmarks(): void {
  try {
    window.localStorage.removeItem(NATURAL_BOOKMARKS_STORAGE_KEY);
  } catch {
    // Abaikan.
  }
}
